import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    GetOrganizationalAttestationGQL,
    UpdateOrganizationalAttestationGQL,
    CreateAndSignOrganizationalAttestationGQL,
    GetAttestationTemplateGQL,
    GetOrganizationalAttestationQuery,
    PagingInput,
    OrganizationalQueryComplexInput,
    OrganizationalSimpleQueryInput,
    OrganizationalUserField,
    ComparatorOperators,
    OperatorInput,
    GetOrganizationalCampaignsGQL,
    GetOrganizationalCampaignsQuery,
    CloseOrganizationalCampaignGQL,
    CloseOrganizationalCampaignMutation,
    CreateOrUpdateOrganizationalCampaignGQL,
    CreateOrUpdateOrganizationalCampaignMutation,
    ExportAttestationTemplateGQL,
    ExportAttestationTemplateQuery,
    GetAttestationTemplateQuery,
    CreateAndSignOrganizationalAttestationMutation,
    AttestationTemplateInput,
    CreateOrUpdateAttestationTemplateGQL,
    CreateOrUpdateAttestationTemplateMutation,
    UpdateOrganizationalAttestationMutation
} from "../../../graphql/generated";

@Injectable({ providedIn: 'root' })
export class OrganizationalIndependenceService {
    private GetOrganizationalAttestationGQL: GetOrganizationalAttestationGQL = inject(GetOrganizationalAttestationGQL);
    private UpdateOrganizationalAttestationGQL: UpdateOrganizationalAttestationGQL = inject(UpdateOrganizationalAttestationGQL);
    private CreateAndSignOrganizationalAttestationGQL: CreateAndSignOrganizationalAttestationGQL = inject(CreateAndSignOrganizationalAttestationGQL);
    private CloseOrganizationalCampaignGQL: CloseOrganizationalCampaignGQL = inject(CloseOrganizationalCampaignGQL);
    private GetAttestationTemplateGQL: GetAttestationTemplateGQL = inject(GetAttestationTemplateGQL);
    private GetOrganizationalCampaignsGQL: GetOrganizationalCampaignsGQL = inject(GetOrganizationalCampaignsGQL);
    private CreateOrUpdateOrganizationalCampaignGQL: CreateOrUpdateOrganizationalCampaignGQL = inject(CreateOrUpdateOrganizationalCampaignGQL)
    private ExportAttestationTemplateGQL: ExportAttestationTemplateGQL = inject(ExportAttestationTemplateGQL);
    private CreateOrUpdateAttestationTemplateGQL: CreateOrUpdateAttestationTemplateGQL = inject(CreateOrUpdateAttestationTemplateGQL);

    getOrganizationalAttestation(campaignId: string, parameters: any): Observable<GetOrganizationalAttestationQuery['getOrganizationalAttestation']> {
        const par = convertLazyLoadToComplex(parameters);
        return this.GetOrganizationalAttestationGQL.fetch({ campaignId, page: par.page, query: par.query }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getOrganizationalAttestation))
    }

    getCampaigns(): Observable<GetOrganizationalCampaignsQuery['getOrganizationalCampaigns']> {
        return this.GetOrganizationalCampaignsGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getOrganizationalCampaigns))
    }

    // Crea o aggiorna una campagna di adherence
    createOrUpdateOrganizationalCampaignGQL(id: string, year: number): Observable<CreateOrUpdateOrganizationalCampaignMutation['createOrUpdateOrganizationalCampaign']> {
        return this.CreateOrUpdateOrganizationalCampaignGQL.mutate({ id, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createOrUpdateOrganizationalCampaign))
    }

    // Chiude una campagna di adherence
    closeAdherenceCampaign(id: string): Observable<CloseOrganizationalCampaignMutation['closeOrganizationalCampaign']> {
        return this.CloseOrganizationalCampaignGQL.mutate({ id }, { fetchPolicy: 'no-cache' }).pipe(map(result => (result.data?.closeOrganizationalCampaign ?? null)));
    }

    exportAttestationTemplate(campaignId: string, timeZone: string, parameters: any): Observable<ExportAttestationTemplateQuery['downloadOrganizationalTemplate']> {
        const par = convertLazyLoadToComplex(parameters);
        return this.ExportAttestationTemplateGQL.fetch({ campaignId, timeZone, query: par.query }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.downloadOrganizationalTemplate));
    }

    getAttestationTemplate(campaignId: string): Observable<GetAttestationTemplateQuery['getAttestationTemplate']> {
        return this.GetAttestationTemplateGQL.fetch({ campaignId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getAttestationTemplate));
    }

    createAndSignAttestation(legalEntityTrackingIds: string[]): Observable<CreateAndSignOrganizationalAttestationMutation['createAndSignOrganizationalAttestation']> {
        return this.CreateAndSignOrganizationalAttestationGQL.mutate({ legalEntityTrackingIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createAndSignOrganizationalAttestation))
    }

    saveTemplate(template: AttestationTemplateInput): Observable<CreateOrUpdateAttestationTemplateMutation['createOrUpdateAttestationTemplate']> {
        return this.CreateOrUpdateAttestationTemplateGQL.mutate({ template }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createOrUpdateAttestationTemplate))
    }

    updateNote(notes: string, legalEntityTrackingId: string, campaignId: string): Observable<UpdateOrganizationalAttestationMutation['updateOrganizationalAttestation']> {
        return this.UpdateOrganizationalAttestationGQL.mutate({ legalEntityTrackingId, notes, campaignId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateOrganizationalAttestation))
    }
}

export function convertLazyLoadToComplex(parameters: any): { query: OrganizationalQueryComplexInput, page: PagingInput } {
    const query: OrganizationalSimpleQueryInput[] = [];

    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_SELF_DASHBOARD[field as keyof typeof ENUM_SELF_DASHBOARD] || [field]).forEach(key => {
                        const val: string[] = values;
                        query.push({
                            field: key as OrganizationalUserField,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: key === OrganizationalUserField.Signed ? val.map(v => v?.toLowerCase() === 'signed' ? 'true' : 'false') : key === OrganizationalUserField.LegalEntityCode ? val.map(v => v?.split(/-(.+)/)[0]?.trim()) : val.map(v => v?.toLowerCase() === 'empty' ? null : v)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 10
        }
    }
}


export const ENUM_SELF_DASHBOARD = {
    businessUnit: [OrganizationalUserField.BusinessUnit],
    organizationalUnit: [OrganizationalUserField.OrganizationalUnit],
    legalEntityName: [OrganizationalUserField.LegalEntityCode],
    status: [OrganizationalUserField.Status],
    positionHeld: [OrganizationalUserField.PositionHeld],
    signed: [OrganizationalUserField.Signed],
    timestamp: [OrganizationalUserField.SignTimestamp],
    notes: [OrganizationalUserField.Notes],
    auditControlApproach: [OrganizationalUserField.AuditControlApproach],
    startDate: [OrganizationalUserField.StartDate],
    endDate: [OrganizationalUserField.EndDate],
    chiefAuditor: [OrganizationalUserField.ChiefAuditor]
}